/**
 * 牌桌上的聊天气泡（牌桌嘴炮）。
 *
 * 三条结构性保证：
 *  - **在所有牌局元素之下**（`--z-say: 6`，低于底池、公共牌、座位、下注、手牌、行动气泡、结算卡）。
 *    最坏的情况是气泡自己被挡一角，绝不会挡住牌、挡住输赢。
 *  - **给行动气泡让位**：同一座位正在播「跟注 200」时先隐去，播完再回来；剩不到 0.8 秒就不回来了。
 *  - **只冒新鲜事**：只订真正应用了的消息，且服务端时间超过 6 秒的一律不冒 ——
 *    重连、断线补发都不会补播一串旧话。快照里的 chatTail 从来不冒气泡。
 *
 * 落点由几何层算好（`SeatAnchor.say`），这里零量测。
 */
import { useEffect, useReducer, useRef } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import type { SeatIndex, ServerMessage } from '@poker/shared'

import { useFx, useMotionProfile } from '../fx/FxLayer.js'
import { useRoomLink } from '../room/roomLink.js'
import { useSettingsStore, useUiStore } from '../store/index.js'
import { useTableLayout } from '../table/TableScene.js'
import { useNetStore } from '../transport/applyMessage.js'
import { SayBoard } from './sayBoard.js'
import { isFreshEvent, type SayKind } from './talkDerive.js'
import './talk.css'

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}

export function SayLayer(): ReactNode {
  const layout = useTableLayout()
  const link = useRoomLink()
  const fx = useFx()
  const enabled = useSettingsStore((s) => s.chatBubbles)
  const veiled = useUiStore((s) => s.veiled)
  const reduced = useMotionProfile().reduced
  const [, rerender] = useReducer((x: number) => x + 1, 0)

  const boardRef = useRef<SayBoard>(new SayBoard())
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const layoutRef = useRef(layout)
  layoutRef.current = layout

  // 只挂一个定时器，指向最近的那个状态变化
  const pumpRef = useRef<() => void>(() => undefined)
  pumpRef.current = (): void => {
    if (timerRef.current !== null) clearTimeout(timerRef.current)
    timerRef.current = null
    const board = boardRef.current
    if (board.advance(now())) rerender()
    const at = board.nextAt()
    if (at !== null) timerRef.current = setTimeout(() => pumpRef.current(), Math.max(0, at - now()) + 4)
  }

  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current)
    },
    [],
  )

  // ── 入口：聊天 / 催一下 ──
  useEffect(() => {
    const add = (seat: SeatIndex, text: string, kind: SayKind): void => {
      boardRef.current.add(seat, text, kind, now())
      rerender()
      pumpRef.current()
    }

    const onMsg = (m: ServerMessage, st: number): void => {
      if (useUiStore.getState().resync) return
      if (!isFreshEvent(st, link.clock.now())) return
      const view = useNetStore.getState().view
      if (view === null) return
      const mySeat = view.viewer.ownedSeat
      if (m.type === 'chat') {
        const e = m.entry
        if (e.kind !== 'player' || e.seat === null) return
        // 自己座位只在有干净落点时冒：自己的座位贴着底边，挤进去只会压住自己的底牌
        if (e.seat === mySeat && layoutRef.current?.seatBySeat.get(e.seat)?.say.clean !== true) return
        add(e.seat, e.text, e.phrase === undefined ? 'text' : 'phrase')
        return
      }
      if (m.type === 'emote' && m.emote === 'nudge' && m.seat !== null && m.targetSeat !== undefined) {
        add(m.seat, m.targetSeat === mySeat ? '催你呢' : '快点！', 'nudge')
      }
    }

    const subscribe = link.onApplied ?? link.onMessage
    if (subscribe === undefined) return undefined
    return subscribe((m, frame) => onMsg(m, frame.st))
  }, [link])

  // ── 给行动气泡让位 ──
  useEffect(() => {
    if (fx === null) return undefined
    return fx.onActionBubble((seat, ms) => {
      if (!boardRef.current.yieldSeat(seat, ms, now())) return
      rerender()
      pumpRef.current()
    })
  }, [fx])

  if (layout === null || !enabled || veiled) return null

  return (
    <div className="say-layer" aria-hidden="true" data-reduced={reduced ? 'on' : 'off'}>
      {boardRef.current.bubbles().map((b) => {
        const a = layout.seatBySeat.get(b.seat)?.say
        if (a === undefined) return null
        const style = {
          left: `${String(a.x)}px`,
          top: `${String(a.y)}px`,
          '--say-max': `${String(a.maxW)}px`,
        } as CSSProperties
        return (
          <div
            key={b.key}
            className="say"
            data-dir={a.dir}
            data-align={a.align}
            data-kind={b.kind}
            data-state={b.state}
            style={style}
          >
            <span className="say__body">{b.text}</span>
          </div>
        )
      })}
    </div>
  )
}
